import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { useRouter } from "@shared/client/navigation";
import { Alert, AlertDescription, AlertTitle } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { toastActionClassName } from "@shared/components/ui/sonner";
import { t, useT } from "@shared/client/locale";

/**
 * Les deux travaux longs de la fiche lead : l'enquête (`POST
 * /api/leads/[id]/enrich`, jusqu'à 4 min 30) et la rédaction des messages
 * (`POST /api/leads/[id]/emails`, un appel au modèle). Ils tournent dans la
 * requête HTTP ; leur état vivait dans la fiche, si bien qu'en la fermant ou
 * en changeant d'écran, on ne savait jamais s'ils avaient abouti.
 *
 * Ils vivent donc ici, hors de React : la suite de la requête s'exécute quoi
 * qu'il advienne de l'écran, et une fiche rouverte retrouve son indicateur.
 * L'issue s'annonce par un toast quand on ne regarde pas ce lead (fiche
 * fermée, autre lead, autre écran) ; sinon la fiche l'affiche elle-même — le
 * résultat, ou l'échec dans un encart avec sa reprise (`LeadTaskAlert`).
 */

export type LeadTaskKind = "enrich" | "emails";

type LeadRef = { id: string; name: string };

// Copy of each task (done, failed, interrupted, retry): `leads.tasks.<kind>`,
// read when it is shown, so that it follows the current language.
const TASKS: Record<LeadTaskKind, { path: string; tab: "message" | null }> = {
  enrich: { path: "enrich", tab: null },
  // Les brouillons sont sur l'onglet Message : « Ouvrir » y mène.
  emails: { path: "emails", tab: "message" },
};

/**
 * Un échec et sa reprise : rejouer (l'API a répondu d'une panne passagère),
 * recharger (la connexion a été coupée : le serveur a pu continuer, relancer
 * dépenserait deux fois), ou rien (une cause à lever d'abord).
 */
export type LeadTaskFailure = { message: string; reprise: "retry" | "reload" | null };

type Outcome = { offerId: string | null } & ({ ok: true } | ({ ok: false } & LeadTaskFailure));

const running = new Set<string>();
/** Dernier échec de chaque travail, montré dans la fiche jusqu'à la relance ou la fermeture. */
const failures = new Map<string, LeadTaskFailure>();
const listeners = new Set<() => void>();
/** Dernier toast de chaque travail, pour qu'une relance efface l'issue précédente. */
const lastToast = new Map<string, string>();
let toastSequence = 0;
/** Le lead dont la fiche est ouverte : son résultat s'y affiche déjà. */
let viewedLeadId: string | null = null;
/** Change à la déconnexion : un travail lancé avant ne s'annonce plus. */
let generation = 0;

function taskKey(kind: LeadTaskKind, leadId: string) {
  return `${kind}:${leadId}`;
}

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function dismissToast(key: string) {
  const id = lastToast.get(key);
  if (!id) return;
  toast.dismiss(id);
  lastToast.delete(key);
}

function viewLead(leadId: string) {
  viewedLeadId = leadId;
  // La fiche montre désormais l'issue : le toast qui l'annonçait n'a plus d'objet.
  for (const kind of Object.keys(TASKS) as LeadTaskKind[]) dismissToast(taskKey(kind, leadId));
}

function leaveLead(leadId: string) {
  if (viewedLeadId !== leadId) return;
  viewedLeadId = null;
  // Fiche fermée : ses échecs, vus, s'effacent. Différé, parce qu'en
  // développement React démonte et remonte aussitôt chaque composant.
  setTimeout(() => {
    if (viewedLeadId === leadId) return;
    let changed = false;
    for (const kind of Object.keys(TASKS) as LeadTaskKind[]) changed = failures.delete(taskKey(kind, leadId)) || changed;
    if (changed) emit();
  }, 0);
}

/** Vrai tant que ce travail tourne sur ce lead, que la fiche ait été fermée ou non. */
export function useLeadTaskRunning(kind: LeadTaskKind, leadId: string) {
  return useSyncExternalStore(subscribe, () => running.has(taskKey(kind, leadId)), () => false);
}

/** Dernier échec de ce travail sur ce lead, tant qu'il n'a pas été relancé. */
export function useLeadTaskFailure(kind: LeadTaskKind, leadId: string) {
  return useSyncExternalStore(subscribe, () => failures.get(taskKey(kind, leadId)) ?? null, () => null);
}

/** La fiche ouverte se déclare : son lead n'a pas besoin de toast. */
export function useViewedLead(leadId: string) {
  useEffect(() => {
    viewLead(leadId);
    return () => leaveLead(leadId);
  }, [leadId]);
}

/**
 * À la déconnexion (navigation sans rechargement) : rien de l'ancien compte
 * ne doit s'annoncer au suivant, ni sur l'écran de connexion.
 */
export function resetLeadTasks() {
  generation += 1;
  running.clear();
  failures.clear();
  lastToast.clear();
  viewedLeadId = null;
  toast.dismiss();
  emit();
}

function interrupted(kind: LeadTaskKind) {
  return t().leads.tasks[kind].interrupted;
}

async function post(kind: LeadTaskKind, leadId: string): Promise<Outcome> {
  let response: Response;
  try {
    response = await fetch(`/api/leads/${leadId}/${TASKS[kind].path}`, { method: "POST" });
  } catch {
    return { ok: false, message: interrupted(kind), reprise: "reload", offerId: null };
  }
  const data = (await response.json().catch(() => null)) as { error?: unknown; offerId?: unknown } | null;
  const offerId = typeof data?.offerId === "string" ? data.offerId : null;
  if (response.ok) return { ok: true, offerId };
  const reason = typeof data?.error === "string" && data.error ? data.error : null;
  if (!reason && response.status >= 500) {
    // Une passerelle a répondu à la place de l'API (délai dépassé, relais
    // coupé) : comme pour une connexion perdue, le serveur a pu continuer.
    return { ok: false, message: interrupted(kind), reprise: "reload", offerId };
  }
  return {
    ok: false,
    message: reason ?? t().leads.tasks.noReason(response.status),
    // Rejouer n'a de sens que pour une panne passagère de l'API : pas pour un
    // lead introuvable, un service non configuré ou une enquête à mener d'abord.
    reprise: response.status >= 500 && response.status !== 503 ? "retry" : null,
    offerId,
  };
}

type OpenTarget = { leadId: string; offerId: string | null; tab: "message" | null };

/**
 * « Ouvrir » : la fiche du lead, dans son offre. La liste des leads ne montre
 * que l'offre consultée, et l'on a pu en changer pendant l'enquête (sélecteur,
 * ⌘K, nouvelle offre) : on y revient d'abord, comme le sélecteur de la topbar.
 */
function OpenLead({ target, toastId }: { target: OpenTarget; toastId: string }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const copy = useT().leads.tasks;

  async function open() {
    toast.dismiss(toastId);
    const shell = queryClient.getQueryData<{ activeOfferId?: string | null }>(["/api/views/app-shell"]);
    if (target.offerId && target.offerId !== shell?.activeOfferId) {
      await fetch("/api/offers/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId: target.offerId }),
      }).catch(() => {});
      await queryClient.invalidateQueries();
    }
    const query = new URLSearchParams({ lead: target.leadId });
    if (target.tab) query.set("tab", target.tab);
    const href = `/app/leads?${query}`;
    // Déjà sur les leads : on remplace l'entrée, pour que « Retour » ne
    // rouvre pas la fiche d'avant.
    if (window.location.pathname === "/app/leads") router.replace(href);
    else router.push(href);
  }

  return (
    <button className={toastActionClassName} onClick={() => void open()} type="button">
      {copy.open}
    </button>
  );
}

/**
 * Lance l'enquête ou la rédaction d'un lead, une seule à la fois par lead.
 * `refresh` recharge les vues et se résout une fois les données là : le
 * client de requêtes est global, il sert encore quand l'écran qui a lancé le
 * travail n'est plus là.
 */
export async function runLeadTask(kind: LeadTaskKind, lead: LeadRef, refresh: () => Promise<unknown>): Promise<void> {
  const key = taskKey(kind, lead.id);
  if (running.has(key)) return;
  const started = generation;
  running.add(key);
  failures.delete(key);
  dismissToast(key);
  emit();

  let outcome: Outcome;
  try {
    outcome = await post(kind, lead.id);
    // Les vues rechargées avant de rendre le bouton : sinon il se rallume un
    // instant sur l'ancien contenu, et un second clic relancerait tout.
    if (generation === started && (outcome.ok || outcome.reprise === "reload")) await refresh().catch(() => {});
  } finally {
    if (generation === started) {
      running.delete(key);
      emit();
    }
  }
  if (generation !== started) return;

  const tasks = t().leads.tasks;
  const copy = tasks[kind];
  const viewing = viewedLeadId === lead.id;
  const target: OpenTarget = { leadId: lead.id, offerId: outcome.offerId, tab: TASKS[kind].tab };
  const toastId = `${key}:${++toastSequence}`;
  if (outcome.ok) {
    if (viewing) return;
    lastToast.set(key, toastId);
    toast.success(`${copy.done} · ${lead.name}`, { id: toastId, action: <OpenLead target={target} toastId={toastId} /> });
    return;
  }

  const { message, reprise } = outcome;
  failures.set(key, { message, reprise });
  emit();
  // La fiche ouverte montre l'échec avec sa reprise, à portée du clavier.
  if (viewing) return;
  lastToast.set(key, toastId);
  toast.error(`${copy.failed} · ${lead.name}`, {
    id: toastId,
    description: reprise === "reload" ? tasks.reloadHint(message) : message,
    // Une raison à lire : un peu plus long qu'une confirmation.
    duration: 10_000,
    action:
      reprise === "retry" ? (
        { label: tasks.retry, onClick: () => void runLeadTask(kind, lead, refresh) }
      ) : (
        <OpenLead target={target} toastId={toastId} />
      ),
  });
}

/**
 * L'échec du travail dans la fiche ouverte : sa cause et sa reprise — la même
 * action que le bouton d'origine, ou un rechargement quand la connexion a été
 * coupée (relancer dépenserait une seconde enquête).
 */
export function LeadTaskAlert({
  kind,
  leadId,
  onRetry,
  className,
}: {
  kind: LeadTaskKind;
  leadId: string;
  /** Absent en lecture seule. */
  onRetry?: () => Promise<void>;
  className?: string;
}) {
  const failure = useLeadTaskFailure(kind, leadId);
  const queryClient = useQueryClient();
  const copy = useT().leads.tasks;
  if (!failure) return null;

  function reload() {
    failures.delete(taskKey(kind, leadId));
    emit();
    void queryClient.invalidateQueries();
  }

  const action =
    failure.reprise === "retry" && onRetry ? (
      <Button onClick={() => void onRetry()} size="sm" type="button" variant="outline">
        {copy[kind].retry}
      </Button>
    ) : failure.reprise === "reload" ? (
      <Button onClick={reload} size="sm" type="button" variant="outline">
        {copy.reload}
      </Button>
    ) : null;

  return (
    <Alert className={className} role="alert" variant="destructive">
      <AlertTitle>{copy[kind].failed}</AlertTitle>
      <AlertDescription>
        <p className="m-0">{failure.message}</p>
        {action && <div className="mt-1.5">{action}</div>}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Lanceur des travaux pour un écran : il recharge les vues par le client de
 * requêtes global, qui survit à l'écran.
 */
export function useLeadTaskRunner() {
  const queryClient = useQueryClient();
  return (kind: LeadTaskKind, lead: LeadRef) => runLeadTask(kind, lead, () => queryClient.invalidateQueries());
}
